import { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthDialog({ open, onClose }) {
  const { t } = useLanguage();
  const { completeAuth, signIn } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const endpoint = mode === "signin" ? "login" : "register";
      const body = mode === "signin" ? { email, password } : { email, password, name };
      const res = await fetch(`${API}/auth/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatError(data.detail) || t("auth.error"));
        return;
      }
      completeAuth(data);
      onClose();
    } catch {
      setError(t("auth.error"));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setError(null);
    if (!email.trim()) {
      setError(t("auth.emailRequired"));
      return;
    }
    setBusy(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setNotice(t("auth.resetSent"));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[96] bg-xir-dark/75 backdrop-blur-sm"
            data-testid="auth-overlay"
          />
          <motion.div
            initial={{ opacity: 0, x: "-50%", y: "-48%", scale: 0.98 }}
            animate={{ opacity: 1, x: "-50%", y: "-50%", scale: 1 }}
            exit={{ opacity: 0, x: "-50%", y: "-48%", scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-1/2 z-[97] w-[min(430px,92vw)] max-h-[85dvh] overflow-y-auto border hairline bg-xir-surface p-8 sm:p-10"
            data-testid="auth-dialog"
            role="dialog"
            aria-label={t("auth.title")}
          >
            <button onClick={onClose} data-testid="auth-close" aria-label={t("nav.close")} className="absolute end-5 top-5 text-xir-paper/50 hover:text-xir-paper">
              <X size={18} strokeWidth={1.5} />
            </button>

            <p className="font-display text-2xl font-medium text-xir-paper">CAFÉXIR</p>
            <p className="mt-1 text-xs tracking-[0.2em] text-xir-paper/50">{t("auth.subtitle")}</p>

            <div className="mt-7 flex border-b hairline" role="tablist">
              {["signin", "signup"].map((m) => (
                <button
                  key={m}
                  role="tab"
                  aria-selected={mode === m}
                  onClick={() => {
                    setMode(m);
                    setError(null);
                    setNotice(null);
                  }}
                  data-testid={`auth-tab-${m}`}
                  className={`flex-1 pb-3 text-xs uppercase tracking-[0.2em] transition-colors ${
                    mode === m ? "border-b border-xir-gold text-xir-gold" : "text-xir-paper/45 hover:text-xir-paper"
                  }`}
                >
                  {t(`auth.${m}`)}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-7 space-y-4">
              {mode === "signup" && (
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("auth.name")}
                  data-testid="auth-name-input"
                  className="w-full border hairline bg-transparent px-4 py-3 text-sm text-xir-paper placeholder:text-xir-paper/35 focus:border-xir-gold/50 focus:outline-none"
                />
              )}
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.email")}
                data-testid="auth-email-input"
                className="w-full border hairline bg-transparent px-4 py-3 text-sm text-xir-paper placeholder:text-xir-paper/35 focus:border-xir-gold/50 focus:outline-none"
              />
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.password")}
                data-testid="auth-password-input"
                className="w-full border hairline bg-transparent px-4 py-3 text-sm text-xir-paper placeholder:text-xir-paper/35 focus:border-xir-gold/50 focus:outline-none"
              />

              {error && (
                <p className="text-xs leading-relaxed text-red-400/90" data-testid="auth-error" role="alert">
                  {error}
                </p>
              )}
              {notice && (
                <p className="text-xs leading-relaxed text-xir-gold" data-testid="auth-notice">
                  {notice}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit"
                className="w-full border border-xir-gold/40 py-3.5 text-xs uppercase tracking-[0.25em] text-xir-gold transition-colors hover:bg-xir-gold hover:text-xir-dark disabled:opacity-40"
              >
                {t(`auth.${mode}`)}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between">
              <button onClick={forgot} data-testid="auth-forgot" className="text-[0.7rem] text-xir-paper/45 underline-offset-4 hover:text-xir-gold hover:underline">
                {t("auth.forgot")}
              </button>
            </div>

            <div className="my-5 flex items-center gap-4">
              <span className="h-px flex-1 bg-xir-gold/15" />
              <span className="text-[0.6rem] uppercase tracking-[0.25em] text-xir-paper/35">{t("auth.or")}</span>
              <span className="h-px flex-1 bg-xir-gold/15" />
            </div>

            <button
              onClick={signIn}
              data-testid="auth-google-button"
              className="w-full border hairline py-3.5 text-xs uppercase tracking-[0.22em] text-xir-paper/80 transition-colors hover:border-xir-gold/40 hover:text-xir-paper"
            >
              {t("auth.google")}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
